import React, { useMemo } from 'react'
import { Color } from '@eduact/student-theme'
import { LayoutProps, SpaceProps } from 'styled-system'
import { useNavigate, generatePath } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import routes from '@Constants/Routes'
import {
	StyledBundleCard,
	StyledBundleImage,
	Badge,
	CardBody,
	Title,
	Instructor,
	InfoWrapper,
	SubjectWrapper,
	SubjectImage,
	Subject,
	CardButton,
	PriceRibbon,
	OldPrice,
	CoursesCount,
} from './BundleCard.styled'

export type BundleCardProps = {
	bundle: Bundle
	onClick?: React.MouseEventHandler
} & LayoutProps &
	SpaceProps

const BundleCard: React.FC<BundleCardProps> = ({ bundle, onClick, ...props }) => {
	const navigate = useNavigate()
	const [t] = useTranslation('bundles')

	const owned = Boolean(bundle.owned)
	const isFree = Number(bundle.price) === 0
	// strike through the sum of individual course prices (fallback old_price)
	const strickenPrice = useMemo(() => {
		const total = Number(bundle.total_courses_price || 0)
		if (total > Number(bundle.price)) return total
		return bundle.old_price ?? undefined
	}, [bundle])

	const buttonLabel = useMemo(() => {
		if (owned) return t('owned')
		if (isFree) return t('free')
		return t('buy_now')
	}, [owned, isFree, t])

	const buttonColor = useMemo((): Color => {
		if (owned) return 'purple'
		return 'primary'
	}, [owned])

	return (
		<StyledBundleCard
			onClick={e => {
				if (!bundle.accessible) return
				if (!onClick) {
					e.stopPropagation()
					navigate(generatePath(routes.bundle, { label: bundle.label }))
				} else {
					onClick(e)
				}
			}}
			{...props}
		>
			<StyledBundleImage src={bundle.thumbnail ?? ''} />
			<Badge>{t('badge')}</Badge>
			{!owned && (
				<PriceRibbon>
					{isFree ? t('free') : `${bundle.price} EGP`}
					{!isFree && strickenPrice ? <OldPrice>{strickenPrice} EGP</OldPrice> : null}
				</PriceRibbon>
			)}
			<CardBody>
				<Title>{bundle.title}</Title>
				<Instructor>
					{bundle.instructor?.user?.first_name} {bundle.instructor?.user?.last_name}
				</Instructor>
				<InfoWrapper>
					<CoursesCount>{t('courses_count', { count: bundle.courses_count ?? bundle.courses?.length ?? 0 })}</CoursesCount>
					{bundle.category && (
						<SubjectWrapper>
							<SubjectImage src={bundle.category.icon ?? ''} />
							<Subject>{bundle.category.name}</Subject>
						</SubjectWrapper>
					)}
				</InfoWrapper>
			</CardBody>
			<CardButton variant={buttonColor}>{buttonLabel}</CardButton>
		</StyledBundleCard>
	)
}

export default BundleCard
